
import { useState, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { generateEmailTemplate } from '@/services/communication/emailTemplates';
import { generateCallScript } from '@/services/communication/callScripts';
import { saveTemplate } from '@/services/communication/templates';
import { getLeadById } from '@/services/leads';
import { Lead } from '@/types/lead';
import { useAuth } from '@/context/AuthContext';

export const useCommunicationTemplates = (leadId: string) => {
  const { toast } = useToast();
  const { profile } = useAuth();
  const [lead, setLead] = useState<Lead | null>(null);
  const [emailTemplate, setEmailTemplate] = useState<string>('');
  const [callScript, setCallScript] = useState<string>('');
  const [isLoadingEmail, setIsLoadingEmail] = useState<boolean>(false);
  const [isLoadingCall, setIsLoadingCall] = useState<boolean>(false);

  useEffect(() => {
    const fetchLead = async () => {
      try {
        const leadData = await getLeadById(leadId);
        if (leadData) {
          setLead(leadData);
        }
      } catch (error) {
        console.error('Error fetching lead details:', error);
      }
    };

    fetchLead();
  }, [leadId]);

  const getSenderInfo = () => {
    return {
      senderName: profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : '',
      senderRole: profile?.job_title || 'Sales Representative',
      senderCompany: profile?.company_id ? 'Your Company' : ''
    };
  };

  const handleGenerateEmailTemplate = async () => {
    if (!leadId) return;
    
    setIsLoadingEmail(true);
    try {
      // Pass the profile information automatically
      const senderInfo = getSenderInfo();
      
      const template = await generateEmailTemplate(leadId, senderInfo);
      if (template) {
        setEmailTemplate(template);
        toast({
          title: "Email Template Generated",
          description: "Your email template has been created successfully."
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to generate email template. Try again later."
        });
      }
    } catch (error) {
      console.error('Error generating email template:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "An error occurred while generating the email template."
      });
    } finally {
      setIsLoadingEmail(false);
    }
  };

  const handleGenerateEmailWithPrompt = async (customPrompt: string) => {
    if (!leadId) return;
    
    setIsLoadingEmail(true);
    try {
      // Pass the profile information and custom prompt
      const senderInfo = getSenderInfo();
      
      const template = await generateEmailTemplate(leadId, senderInfo, customPrompt);
      if (template) {
        setEmailTemplate(template);
        toast({
          title: "Email Template Generated",
          description: "Your customized email template has been created successfully."
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to generate email template. Try again later."
        });
      }
    } catch (error) {
      console.error('Error generating email template with custom prompt:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "An error occurred while generating the email template."
      });
    } finally {
      setIsLoadingEmail(false);
    }
  };

  const handleGenerateCallScript = async () => {
    if (!leadId) return;
    
    setIsLoadingCall(true);
    try {
      // Pass the profile information automatically
      const senderInfo = getSenderInfo();
      
      const script = await generateCallScript(leadId, senderInfo);
      if (script) {
        setCallScript(script);
        toast({
          title: "Call Script Generated",
          description: "Your call script has been created successfully."
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to generate call script. Try again later."
        });
      }
    } catch (error) {
      console.error('Error generating call script:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "An error occurred while generating the call script."
      });
    } finally {
      setIsLoadingCall(false);
    }
  };

  const handleGenerateCallScriptWithPrompt = async (customPrompt: string) => {
    if (!leadId) return;
    
    setIsLoadingCall(true);
    try {
      // Pass the profile information and custom prompt
      const senderInfo = getSenderInfo();
      
      const script = await generateCallScript(leadId, senderInfo, customPrompt);
      if (script) {
        setCallScript(script);
        toast({
          title: "Call Script Generated",
          description: "Your customized call script has been created successfully."
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to generate call script. Try again later."
        });
      }
    } catch (error) {
      console.error('Error generating call script with custom prompt:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "An error occurred while generating the call script."
      });
    } finally {
      setIsLoadingCall(false);
    }
  };

  const saveCurrentEmailTemplate = async (content: string) => {
    if (!content) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No email template content to save."
      });
      return;
    }

    try {
      const templateName = `Email for ${lead?.name || 'lead'} (${lead?.status || 'new'})`;
      const templateData = {
        name: templateName,
        content,
        type: 'email' as const,
        leadStatus: lead?.status,
        leadId  // Include leadId when saving
      };

      const result = await saveTemplate(templateData);
      
      if (result) {
        toast({
          title: "Success",
          description: "Email template saved successfully."
        });
      } else {
        throw new Error("Failed to save template");
      }
    } catch (error) {
      console.error('Error saving email template:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save email template."
      });
    }
  };

  const saveCurrentCallScript = async (content: string) => {
    if (!content) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No call script content to save."
      });
      return;
    }

    try {
      const templateName = `Call script for ${lead?.name || 'lead'} (${lead?.status || 'new'})`;
      const templateData = {
        name: templateName,
        content,
        type: 'call' as const,
        leadStatus: lead?.status,
        leadId  // Include leadId when saving
      };

      const result = await saveTemplate(templateData);
      
      if (result) {
        toast({
          title: "Success",
          description: "Call script saved successfully."
        });
      } else {
        throw new Error("Failed to save call script");
      }
    } catch (error) {
      console.error('Error saving call script:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save call script."
      });
    }
  };

  return {
    lead,
    emailTemplate,
    callScript,
    isLoadingEmail,
    isLoadingCall,
    handleGenerateEmailTemplate,
    handleGenerateEmailWithPrompt,
    handleGenerateCallScript,
    handleGenerateCallScriptWithPrompt,
    saveCurrentEmailTemplate,
    saveCurrentCallScript
  };
};
